import { useCallback, useEffect, useState } from 'react'
import Modal from '../Modal'
import Boton from '../Boton'
import { avisosApi } from '../../api/avisos'
import { fmtFechaHora } from '../../utils/fecha'
import { colors, text, radius, sx } from '../../design/tokens'

/**
 * Card "Avisos" del detalle de un documento (NV / remito / recibo): el hilo
 * completo de avisos sobre ese documento + botón "✉ Avisar" para dejarle uno
 * a un compañero ("imprimilo y mandámelo", "seguí el tema con el cliente").
 *
 * Sin avisos, la card se reduce al botón — no ocupa lugar hasta que hace falta.
 */
export default function AvisosCard({ tipo, id, style }) {
  const [avisos, setAvisos] = useState([])
  const [abrirNuevo, setAbrirNuevo] = useState(false)

  const cargar = useCallback(() => {
    avisosApi.porEntidad(tipo, id).then(({ data }) => setAvisos(data || [])).catch(() => {})
  }, [tipo, id])

  useEffect(() => { cargar() }, [cargar])

  return (
    <div style={{ ...st.card, ...style }}>
      <div style={st.head}>
        <h3 style={sx.cardTitle}>Avisos</h3>
        <Boton variante="secondary" sm onClick={() => setAbrirNuevo(true)}>✉ Avisar a un compañero</Boton>
      </div>

      {avisos.length > 0 && (
        <div style={st.lista}>
          {avisos.map(a => (
            <div key={a.id} style={st.item}>
              <div style={st.itemHead}>
                <span style={st.quien}>
                  <strong>{a.de?.nombre}</strong> → {a.para?.nombre}
                </span>
                <span style={st.estado(a.estado)}>
                  {a.estado === 'resuelto' ? '✓ Hecho' : a.estado === 'leido' ? 'Leído' : 'Sin leer'}
                </span>
              </div>
              <div style={st.mensaje}>“{a.mensaje}”</div>
              <div style={st.fecha}>
                {fmtFechaHora(a.created_at)}
                {a.resuelto_at ? ` · resuelto ${fmtFechaHora(a.resuelto_at)}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {abrirNuevo && (
        <ModalNuevoAviso
          tipo={tipo}
          id={id}
          onCreado={() => { setAbrirNuevo(false); cargar() }}
          onClose={() => setAbrirNuevo(false)}
        />
      )}
    </div>
  )
}

/** Form mínimo: a quién + el mensaje. El documento viene dado por la pantalla. */
function ModalNuevoAviso({ tipo, id, onCreado, onClose }) {
  const [destinatarios, setDestinatarios] = useState([])
  const [paraId, setParaId] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    avisosApi.destinatarios().then(({ data }) => setDestinatarios(data || [])).catch(() => {})
  }, [])

  const enviar = async () => {
    if (!paraId || !mensaje.trim()) { setError('Elegí a quién avisar y escribí el mensaje.'); return }
    setEnviando(true); setError('')
    try {
      await avisosApi.crear({
        para_usuario_id: Number(paraId),
        entidad_tipo: tipo,
        entidad_id: id,
        mensaje: mensaje.trim(),
      })
      onCreado()
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo crear el aviso.')
      setEnviando(false)
    }
  }

  return (
    <Modal title="✉ Avisar a un compañero" onClose={onClose} maxWidth={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={st.label}>
          ¿A quién?
          <select value={paraId} onChange={e => setParaId(e.target.value)} style={st.select}>
            <option value="">Elegí a la persona…</option>
            {destinatarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        </label>
        <label style={st.label}>
          Mensaje
          <textarea
            value={mensaje}
            onChange={e => setMensaje(e.target.value)}
            rows={3}
            placeholder='Ej: "Imprimilo y mandámelo" · "Seguí el tema con el cliente"'
            style={st.textarea}
          />
        </label>
        <p style={st.hint}>
          Le va a aparecer como ventana emergente en LGR hasta que lo marque leído
          (y por Telegram si tiene el bot vinculado).
        </p>
        {error && <p style={st.error}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton variante="primary" disabled={enviando} onClick={enviar}>
            {enviando ? 'Enviando…' : 'Dejar el aviso'}
          </Boton>
        </div>
      </div>
    </Modal>
  )
}

const st = {
  card: {
    background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.md,
    padding: '14px 16px', marginTop: 16,
  },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  lista: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  item: { border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm, padding: '8px 10px', background: colors.bgSoft },
  itemHead: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' },
  quien: { fontSize: text.sec, color: colors.textSec },
  estado: (estado) => ({
    fontSize: text.meta, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    color: estado === 'resuelto' ? colors.ok : estado === 'leido' ? colors.info : colors.warn,
  }),
  mensaje: { marginTop: 4, fontSize: text.body, color: colors.text, whiteSpace: 'pre-wrap' },
  fecha: { marginTop: 4, fontSize: text.meta, color: colors.textMeta },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: text.sec, fontWeight: 600, color: colors.textSec },
  select: { padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit' },
  textarea: { padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', resize: 'vertical' },
  hint: { margin: 0, fontSize: text.meta, color: colors.textMeta },
  error: { margin: 0, fontSize: text.sec, color: colors.err, fontWeight: 600 },
}
